import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";

interface Override {
  path: string;
  title: string | null;
  description: string | null;
  keywords: string[] | null;
  faqs: { question: string; answer: string }[] | null;
  og_title: string | null;
  og_description: string | null;
  og_image: string | null;
  og_url: string | null;
}

/**
 * Route-aware SEO override layer. Reads `seo_overrides` for the current
 * pathname and injects Helmet tags that replace per-page defaults.
 * Helmet dedupes <meta> by name/property, so these win for JS crawlers.
 */
const SeoOverrides = () => {
  const { pathname } = useLocation();
  const [data, setData] = useState<Override | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    (async () => {
      const { data } = await supabase
        .from("seo_overrides")
        .select("path,title,description,keywords,faqs,og_title,og_description,og_image,og_url")
        .eq("path", pathname)
        .maybeSingle();
      if (!cancelled && data) setData(data as unknown as Override);
    })();
    return () => { cancelled = true; };
  }, [pathname]);

  if (!data) return null;

  const faqSchema = data.faqs && data.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  } : null;

  const ogTitle = data.og_title || data.title;
  const ogDesc = data.og_description || data.description;

  return (
    <Helmet>
      {data.title && <title>{data.title}</title>}
      {ogTitle && <meta property="og:title" content={ogTitle} />}
      {ogTitle && <meta name="twitter:title" content={ogTitle} />}
      {data.description && <meta name="description" content={data.description} />}
      {ogDesc && <meta property="og:description" content={ogDesc} />}
      {ogDesc && <meta name="twitter:description" content={ogDesc} />}
      {data.og_image && <meta property="og:image" content={data.og_image} />}
      {data.og_image && <meta name="twitter:image" content={data.og_image} />}
      {data.og_url && <meta property="og:url" content={data.og_url} />}
      {data.og_url && <link rel="canonical" href={data.og_url} />}
      {data.keywords && data.keywords.length > 0 && (
        <meta name="keywords" content={data.keywords.join(", ")} />
      )}
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
    </Helmet>
  );
};

export default SeoOverrides;
